import { useEffect, useRef } from "react";
import type { KeyboardEvent, ReactNode } from "react";

// The app's ONE rejection gesture, shared with the import page's
// add-to-search CTA since 2026-09-15. Lifted rather than copied: the
// amplitudes were tuned by watching it and a twin would drift on the first
// adjustment.
import { runShake } from "./shake";

export interface HighlightOption<V extends string = string> {
  value: V;
  /** Full-sentence label (1-2 sentences). */
  label: ReactNode;
  /** Optional second line under the label. */
  description?: ReactNode;
  /** Rendered at the row's trailing edge, OUTSIDE the activation area —
   *  this is the nested-interactive escape hatch (links, buttons). */
  trailing?: ReactNode;
  disabled?: boolean;
  title?: string;
  testId?: string;
}

export interface HighlightOptionGroupProps<V extends string = string> {
  options: readonly HighlightOption<V>[];
  /** `null` = UNDECIDED. A legal, rendered state — see the docstring rule. */
  value: V | null;
  onChange: (value: V) => void;
  /** REQUIRED — an unnamed radiogroup announces as nothing. */
  "aria-label": string;
  "data-testid"?: string;
  className?: string;
  /**
   * Wobble ONE option to say "that answer did not take".
   *
   * `value` picks the row; `nonce` is what makes a REPEATED rejection move —
   * every new number replays the animation, and the same number never replays
   * it. The caller increments it per answer, so two identical verdicts in a row
   * still produce two wobbles.
   *
   * It shakes the OPTION, not the card and not the column (operator,
   * 2026-09-10). The reading is the macOS password field: the answer you gave
   * is the thing being rejected, so the thing that moves is the answer.
   */
  shake?: { value: V; nonce: number } | null;
}

/**
 * HighlightOptionGroup — a one-of-N choice whose options are FULL-WIDTH ROWS
 * carrying a sentence, selected by HIGHLIGHTING the row. Added 2026-09-10 for
 * the forced-decision conflict cards; generic on purpose — nothing in it knows
 * about conflicts.
 *
 * It is a THIRD thing, not a variant of the two idioms it sits between:
 *  - `SegmentedControl` has the right ARIA (radiogroup/radio) and the wrong
 *    shape: a segment carrying two sentences stops reading as a button, and a
 *    pressed segment does not read as "decided" across a screen of 30 rows.
 *  - the `<label>`-wrapped checkbox row (web's `CompanyChangeRow`) has the right
 *    shape — a big hit area with a hover fill — and the wrong semantics: a
 *    checkbox is binary and independent, these options are mutually exclusive.
 *
 * WHY THE SELECTED STATE IS A FILL AND NOT A PRESSED STATE. The caller's real
 * screen is a queue: someone sweeping N rows has to see WHICH ARE DECIDED
 * without reading any of them, and that is also what makes a blocked
 * "N rows remain" message actionable — they can find the N.
 *
 * UNSELECTED IS A LEGAL, RENDERED STATE, and that is the one property this
 * primitive deliberately does NOT share with `SegmentedControl`.
 * `value={null}` renders every option with `aria-checked="false"` and marks the
 * group `aria-required` — "you must answer" rather than "here is what we
 * answered for you". The alternative that `SegmentedControl`'s docstring
 * recommends — model a default option, e.g. "Do nothing" — was REMOVED from
 * this product on purpose (nu-assist fu5 sub-task 02): a neutral default made
 * "nobody looked at this row" and "somebody looked and chose to leave it" the
 * same value, and the whole point of the forced decision is that those two are
 * different. DO NOT reintroduce a default option to "fix" the unselected state.
 * If a caller needs a no-op answer, it must be a real, named answer with its
 * own sentence, not the absence of one.
 *
 * Keyboard: FOCUS-THEN-ACTIVATE. Arrow/Home/End move DOM FOCUS ONLY; Space and
 * Enter activate the focused option. Selection-follows-focus is not merely
 * unfashionable here, it is dangerous: this control's real options are one-shot
 * persisted COMMANDS (a `Skip` that writes a dismissal, an "I fixed it" that
 * fires an external read), and arrowing past them would execute them while the
 * user is merely browsing (#838 iter-2, A-N1 + C-N2 + C-2). Disabled options
 * stay focusable so their honest `title` is keyboard-reachable; activating one
 * is a no-op. The roving tab stop is the selected option, or — when nothing is
 * selected — the first enabled one, so Tab enters an undecided group at the top.
 *
 * `trailing` is the NESTED-INTERACTIVE ESCAPE HATCH, and it is not cosmetic.
 * The activation area is one element spanning the row, so anything interactive
 * placed INSIDE it (a deep link, a secondary button) would fire twice on a
 * click and the gaps around it would silently activate the option. Same rule
 * the `CompanyChangeRow` label follows, for the same reason. Put links and
 * buttons in `trailing`; it renders as the activating element's SIBLING.
 *
 * Design-system notes (v17 tokens only, no inline styles):
 *  - selected: `bg-accent-muted` fill + `border-accent` + primary ink, plus a
 *    filled radio dot, so the row reads as chosen from a glance and from a
 *    screenshot;
 *  - unselected: `bg-bg` on the card's `bg-surface`, secondary ink, hover
 *    raises to `bg-surface`;
 *  - disabled: `opacity-50` + `cursor-not-allowed`, `aria-disabled` (NOT native
 *    `disabled`, which drops the element out of the tab order and kills the
 *    tooltip in some browsers) with the click/key guards enforcing the off
 *    state.
 */
export function HighlightOptionGroup<V extends string = string>({
  options,
  value,
  onChange,
  className,
  shake = null,
  ...rest
}: HighlightOptionGroupProps<V>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  // Keyed on the NONCE alone. Including the value would replay the wobble when
  // the caller re-renders with the same rejection for a different reason, and
  // omitting the effect entirely (animating inside the click handler) would
  // miss the case the shake exists for: the verdict arrives from the server,
  // long after the click.
  const shakeNonce = shake?.nonce ?? 0;
  const shakeValue = shake?.value ?? null;
  // `options` is intentionally absent from the deps: the callers build the
  // array inline, so its identity changes on every parent render and including
  // it would replay the wobble on re-renders that carry no new verdict. The
  // suppression is biome's — the eslint-flavoured one that stood here silenced
  // nothing, since biome is what `pnpm lint` runs.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the nonce by design — see above
  useEffect(() => {
    if (!shakeNonce || shakeValue === null) return;
    const idx = options.findIndex((o) => o.value === shakeValue);
    const el = refs.current[idx];
    if (el) runShake(el);
  }, [shakeNonce, shakeValue]);
  const selectedIdx = options.findIndex((o) => o.value === value);
  const firstEnabled = options.findIndex((o) => !o.disabled);
  // `value === null` (undecided) falls through to the first enabled option, so
  // Tab enters an unanswered group at its top rather than nowhere at all.
  const tabbableIdx =
    selectedIdx >= 0 && !options[selectedIdx]?.disabled ? selectedIdx : firstEnabled;
  const focusAt = (idx: number) => refs.current[idx]?.focus();
  const onKeyDown = (e: KeyboardEvent, idx: number) => {
    const n = options.length;
    if (n === 0) return;
    let target: number | null = null;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") target = (idx + 1) % n;
    else if (e.key === "ArrowUp" || e.key === "ArrowLeft") target = (idx - 1 + n) % n;
    else if (e.key === "Home") target = 0;
    else if (e.key === "End") target = n - 1;
    else if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      const opt = options[idx];
      if (opt && !opt.disabled && opt.value !== value) onChange(opt.value);
      return;
    }
    if (target === null) return;
    e.preventDefault();
    focusAt(target);
  };

  return (
    <div
      role="radiogroup"
      aria-label={rest["aria-label"]}
      // The group ANNOUNCES that an answer is owed. Without it, an undecided
      // group and a group whose answer happens to be off-screen are the same
      // thing to a screen reader.
      aria-required="true"
      data-testid={rest["data-testid"]}
      className={["flex w-full flex-col gap-1.5", className].filter(Boolean).join(" ")}
    >
      {options.map((opt, idx) => {
        const selected = opt.value === value;
        return (
          <div key={opt.value} className="flex w-full items-stretch gap-2">
            <button
              type="button"
              // biome-ignore lint/a11y/useSemanticElements: a native <input
              // type="radio"> cannot carry a full-width sentence row with a
              // per-option tooltip and an aria-disabled-but-focusable state
              // without a hidden-input+label reimplementation of exactly this.
              // button[role=radio] + aria-checked inside role=radiogroup is the
              // ARIA radio-group pattern (same precedent as SegmentedControl).
              role="radio"
              // FALSE, not undefined, on every option when `value` is null:
              // "there are N answers and none is chosen" is the state, and an
              // absent attribute would report a broken radio instead.
              aria-checked={selected}
              // The testId rides the ACTIVATING element — the row-wide button —
              // rather than the flex wrapper, so a test that clicks it exercises
              // the real target and reads `aria-checked` off the same node.
              data-testid={opt.testId}
              aria-disabled={opt.disabled || undefined}
              tabIndex={idx === tabbableIdx ? 0 : -1}
              ref={(el) => {
                refs.current[idx] = el;
              }}
              title={opt.title}
              className={[
                "flex min-w-0 flex-1 items-start gap-2.5 rounded-md border px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                selected
                  ? "border-accent bg-accent-muted text-text"
                  : "border-border bg-bg text-text-secondary hover:bg-surface hover:text-text",
                opt.disabled
                  ? "cursor-not-allowed opacity-50 hover:bg-bg hover:text-text-secondary"
                  : "",
              ].join(" ")}
              onClick={() => {
                if (!selected && !opt.disabled) onChange(opt.value);
              }}
              onKeyDown={(e) => onKeyDown(e, idx)}
            >
              {/* The dot is REDUNDANT with the fill on purpose: the fill is the
                  glanceable signal, the dot is the one that survives a
                  high-contrast mode or a colour-blind reader who cannot tell
                  `accent-muted` from `bg`. */}
              <span
                aria-hidden="true"
                className={[
                  "mt-0.5 size-3.5 shrink-0 rounded-full border-2",
                  selected ? "border-accent bg-accent" : "border-border bg-transparent",
                ].join(" ")}
              />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className={selected ? "text-label font-semibold" : "text-label"}>
                  {opt.label}
                </span>
                {opt.description ? (
                  <span className="text-caption text-text-secondary">{opt.description}</span>
                ) : null}
              </span>
            </button>
            {/* OUTSIDE the button, never inside it. See the docstring. */}
            {opt.trailing ? <div className="flex shrink-0 items-center">{opt.trailing}</div> : null}
          </div>
        );
      })}
    </div>
  );
}
